import React, {useEffect, useContext} from "react";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import {FormattedMessage, useIntl} from "react-intl";
import {PusherContext} from "contexts/pusherContext";
import {API_TYPE_TO_MENU_KEY} from "domain/components/merchandising/batchActions/batchActionTypes";

export default function RunningActions({runningActions, refreshHandler, reloadHandler}) {
    const { batchActionChannel } = useContext(PusherContext);
    const intl = useIntl();

    useEffect(() => {
        if (!batchActionChannel) return;

        // Make sure this is only called once
        batchActionChannel.bind('action_completed', (data) => {
            // A rank edit pushes this event too (PushRankEditCompleted, data
            // ["ranking"]), but the row already displays the value the user
            // typed: reloading would reorder the list under their feet.
            if (data === 'ranking' || (Array.isArray(data) && data.includes('ranking'))) {
                return;
            }

            // The action is fully processed: drop its banner and refresh the
            // list to show the updated products.
            refreshHandler();
            reloadHandler();
        });
    }, [batchActionChannel]);

    return runningActions.map(action => {
        const menuKey = API_TYPE_TO_MENU_KEY[action.actionType] ?? action.actionType;
        const actionLabel = intl.formatMessage({id: "merchandising.show.label.actions." + menuKey});
        // an action launched from a retailer product list carries its
        // retailer; the merchandising ones show their origin instead
        const origin = action.retailerName
            ?? intl.formatMessage({id: "merchandising.show.label.actions.origin_merchandising"});
        // totalItems stays 0 until the search fan-out has counted the
        // matching products: no percentage to show yet
        const counted = action.totalItems > 0;
        const percent = counted
            ? Math.min(100, Math.round((action.processedItems / action.totalItems) * 100))
            : 0;

        return (
            <Alert key={action.id} severity="info" sx={{ marginBottom: '10px' }}>
                <AlertTitle>
                    <FormattedMessage id={"merchandising.show.label.actions.running_title"} />
                </AlertTitle>
                <FormattedMessage
                    id={counted
                        ? "merchandising.show.label.actions.running_details"
                        : "merchandising.show.label.actions.running_preparing"}
                    values={{
                        actionType: actionLabel,
                        origin: origin,
                        user: action.userFullName,
                        total: action.totalItems,
                        percent: percent,
                    }}
                />
                <LinearProgress
                    variant={counted ? "determinate" : "indeterminate"}
                    value={percent}
                    sx={{ marginTop: '10px' }}
                />
            </Alert>
        );
    });
}
